import React, { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, XCircle, HelpCircle } from 'lucide-react';

const MythsAndFAQPage = () => {
  const myths = [
    {
      myth: 'Vistiek visos išrūšiuotos atliekos supilamos į vieną šiukšlių mašiną',
      truth: 'Dauguma antrines žaliavas surenkančiu sunkvežimių šiai dienai jau turi atskiras sekcijas skirtingoms atliekų rūšims – plastikui, popieriui, stiklui. Visos iš rūšiavimo konteinerių surinktos atliekos rūšiavimo stotyje yra dar kartą atidžiai perrušiuojamos.',
      status: false,
    },
    {
      myth: 'Rūšiuosiu nuo naujų metų, nuo kito mėnesio...',
      truth: 'Geriausia pradėti šiandien! Kiekvienas rūšiuotas daiktas prisideda prie švarios aplinkos. Tausokime savo planetą – ją mums perdavė ne tėvai, o paskolino mūsų vaikai.',
      status: false,
    },
    {
      myth: 'Vieną dieną surenka rūšiuojamas atliekas, kitą – jau buitines',
      truth: 'Kai kurios atliekų tvarkymo įmonės tuo pačiu automobiliu vieną dieną surenka mišrias buitines atliekas, o kitą – jūsų išrūšiuotąsias. Tas pats automobilis – skirtingoms atliekoms surinkti.',
      status: false,
    },
    {
      myth: 'Pradėsiu šiukšles plaut, kad galėčiau jas rūšiuot',
      truth: 'Rūšiavimui plauti ar skalauti pakuočių nėra būtina. Tai labiau rekomendacija, patarimas. Pakanka pašalinti maisto likučius ir skysčius, bet plauti nereikia.',
      status: false,
    },
    {
      myth: 'Prie namų nėra rūšiavimo konteinerio, toli nešti...',
      truth: 'Galimybių – kaip ir kur rūšiuoti – turime. Jei nori rūšiuoti – ieškai ir randi galimybių, jei nenori – ieškai argumentų to nedaryti.',
      status: false,
    },
  ];

  const faqs = [
    {
      question: 'Kodėl aš turiu rūšiuoti į atskirus konteinerius?',
      answer: 'Kartu su buitinėmis atliekomis metamos antrinės žaliavos tampa bevertėmis atliekomis – jos užteršiamos maisto, chemikalų likučiais, stiklo duženom ir jų perdirbti jau nebeįmanoma. Todėl labai svarbu rūšiuoti atskirai.',
    },
    {
      question: 'Ar galima rūšiuoti tik sausas pakuotes, ar jos gali būti ir drėgnos?',
      answer: 'Pakuotės gali būti drėgnos: perdirbant jos yra smulkinamos ir plaunamos. Svarbu, kad jos nebūtų smarkiai suteptos maisto likučiais.',
    },
    {
      question: 'Kur mesti stiklainių dangtelius, stiklinių butelių metalinius kamštelius?',
      answer: 'Dangteliai ir kamšteliai metami į metalo ir plastiko pakuočių atliekų konteinerį (geltoną).',
    },
    {
      question: 'Kur mesti kiaušinių pakuotes?',
      answer: 'Popierinės pakuotės gali būti metamos popieriaus ir kartono pakuočių konteinerį, o plastikinės – į plastiko ir metalo pakuočių konteinerį.',
    },
    {
      question: 'Ar galima į plastikį/popierį maišelį sudėti daug plastikinių/popierinių atliekų?',
      answer: 'Taip, perdirbimo procesui tai skirtumo nesudaro, tad darykite kaip jums patogiau. Svarbu, kad atliekos būtų išrūšiuotos teisingai.',
    },
    {
      question: 'Kur mesti voką su burbuliukais? O kur voką su plastikiniu langeliu?',
      answer: 'Šie kombinuoti vokai metami į plastiko ir metalo pakuočių konteinerį (geltoną).',
    },
    {
      question: 'Kaip rūšiuoti putplastį?',
      answer: 'Jei tai – pakavimo medžiaga, gali būti metama į plastiko ir metalo pakuočių konteinerį. Jei tai statybinės atliekos - išvežama į stambiagabaričių atliekų surinkimo aikšteles.',
    },
    {
      question: 'Ar flomasteriai rūšiuojami? O spalvoti pieštukai?',
      answer: 'Ne, šios kanceliarinės priemonės yra nerūšiuojamos. Jas meskite į mišrių atliekų konteinerį.',
    },
    {
      question: 'Kur keliauja sudegusios kapinių žvakės?',
      answer: 'Dažniausiai šalia kapinių yra specialūs, kapinių atliekoms skirti konteineriai. O jei jų nėra – žvakes meskite į mišrių atliekų konteinerius.',
    },
    {
      question: 'Ar pakuotės, kuriose buvo kosmetikos priemonės yra užterštos?',
      answer: 'Šias pakuotes privaloma rūšiuoti, tačiau prieš tai darant reikia ištuštinti. Produkto likučius reikia išmesti į mišrių atliekų konteinerį.',
    },
    {
      question: 'Ar galima rūšiuoti vaistų pakuotes?',
      answer: 'Taip, maisto papildų, vaistų pakuotes, įskaitant tablečių lapelius galima rūšiuoti. Tik svarbu įsitikinti ar juose nėra likusių tablečių. Pačius vaistus reikia priduoti vaistinėse.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Header */}
      <section className="pt-24 pb-12 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
              Klausimai ir Atsakymai
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
              Mitai ir DUK
            </h1>
            <p className="text-lg text-muted-foreground">
              Paneigkime mitus ir atsakykime į dažniausiai užduodamus klausimus
            </p>
          </div>
        </div>
      </section>

      {/* Myths Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">
              Rūšiavimo Mitai
            </h2>
            <div className="space-y-6">
              {myths.map((item, index) => (
                <Card key={index} className="hover-lift overflow-hidden">
                  <div className="h-1 bg-destructive"></div>
                  <CardHeader>
                    <div className="flex items-start space-x-3">
                      <XCircle className="w-6 h-6 text-destructive flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <Badge variant="destructive" className="mb-3">
                          Mitas
                        </Badge>
                        <CardTitle className="text-xl mb-3">{item.myth}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Alert className="border-primary/20 bg-primary/5">
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <AlertDescription className="text-base">
                        <strong className="text-primary">Tiesa:</strong> {item.truth}
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-muted">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">
                Dažnai Užduodami Klausimai
              </h2>
              <p className="text-lg text-muted-foreground">
                Atsakymai į populiariausius klausimus apie atliekų rūšiavimą
              </p>
            </div>

            <Card>
              <CardContent className="pt-6">
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left hover:text-primary">
                        <div className="flex items-start space-x-3">
                          <HelpCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                          <span className="font-semibold">{faq.question}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-8 pt-2">
                          <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="bg-gradient-hero text-white border-0">
              <CardHeader>
                <CardTitle className="text-2xl text-center">
                  Vis dar turite klausimų?
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <p className="text-white/90 leading-relaxed">
                  Jei neradote atsakymo į savo klausimą, peržiūrėkite visą rūšiavimo ABC skyrių 
                  arba susisiekite su vietos atliekų tvarkymo įmone.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <a href="/rusiavimo-abc">
                    <button className="px-6 py-3 bg-white text-primary rounded-lg font-semibold hover:bg-white/90 transition-smooth">
                      Rūšiavimo ABC
                    </button>
                  </a>
                  <a href="https://gamtosateitis.lt" target="_blank" rel="noopener noreferrer">
                    <button className="px-6 py-3 bg-white/10 text-white border-2 border-white/30 rounded-lg font-semibold hover:bg-white/20 transition-smooth">
                      Gamtos Ateitis
                    </button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MythsAndFAQPage;